import { useEffect, useState } from "react";
import { Loader2, Activity, Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { getActivity } from "@/lib/adminCmsApi";
import { toast } from "sonner";

const ACTION_ICON = { create: Plus, update: Pencil, delete: Trash2 };
const ACTION_CLS = {
  create: "bg-[#BEE7F5] text-[#0E2A57]",
  update: "bg-[#FFF3CD] text-[#856404]",
  delete: "bg-[#FDE2E2] text-[#9B1C1C]",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IE", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminActivity() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getActivity(200);
      setRows(data || []);
    } catch (e) {
      toast.error("Failed to load activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <AdminShell title="Activity Log">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-ags-text-muted max-w-xl">
          Audit trail of every create, update, and delete across the CMS. Shows actor, target, and timestamp.
        </p>
        <button
          onClick={load}
          data-testid="activity-refresh"
          className="inline-flex items-center gap-2 border border-[#0E2A57]/30 text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-[#0E2A57]/15 p-12 flex items-center justify-center text-ags-text-muted">
          <Loader2 size={20} className="animate-spin mr-3" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#0E2A57]/15 p-16 text-center">
          <Activity size={28} className="mx-auto text-[#0E2A57]/30 mb-4" />
          <div className="font-heading font-bold text-lg text-ags-ink">No activity yet</div>
          <p className="text-sm text-ags-text-muted mt-2">Every CMS change will appear here.</p>
        </div>
      ) : (
        <ul className="bg-white border border-[#0E2A57]/15 divide-y divide-ags-hairline">
          {rows.map((r) => {
            const Icon = ACTION_ICON[r.action] || Activity;
            return (
              <li
                key={r.id}
                data-testid={`activity-row-${r.id}`}
                className="px-5 md:px-6 py-4 flex flex-wrap items-center gap-4 hover:bg-ags-cream"
              >
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${ACTION_CLS[r.action] || "bg-[#E5EBF2] text-ags-text-muted"}`}>
                  <Icon size={11} /> {r.action}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-sm text-ags-ink">
                    {r.collection} {r.summary && <span className="text-ags-text-muted font-normal">· {r.summary}</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-ags-text-muted truncate">
                    <span className="font-mono">{r.item_id}</span>
                  </div>
                </div>
                <div className="text-xs text-ags-text-muted font-mono">{r.actor_email}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/45 w-32 text-right">
                  {formatDate(r.created_at)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </AdminShell>
  );
}
